import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { WatchLater, Visibility } from '@mui/icons-material';
import AIConfidenceBadge from '../AIConfidenceBadge';
import {
  formatVolume,
  formatRiskPct,
  getWaitingForKeys,
  cellDense,
  stickyLeft,
  stickyLayer,
  stickyBodyBg,
  STICKY_HEADER_Z,
  STICKY_BODY_Z,
  STICKY_BG_HOVER,
} from './utils';

const thSx = {
  py: 0.75,
  fontWeight: 700,
  fontSize: '0.65rem',
  letterSpacing: '0.04em',
  color: '#1e3a5f',
  textTransform: 'uppercase',
  backgroundColor: '#eff6ff',
};

const WatchlistTable = ({ results }) => {
  const { t, i18n } = useTranslation();

  const watching = (results?.results || []).filter(
    (r) => r.setup_flag && !r.trigger_flag && !r.early_trigger_flag
  );

  if (watching.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        mb: 3,
        overflow: 'hidden',
        border: '1.5px solid #bfdbfe',
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          borderBottom: '1px solid #93c5fd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Visibility sx={{ fontSize: 16, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, color: '#1e3a5f', fontSize: '0.95rem', lineHeight: 1.2 }}>
              {t('earlyPivot.watchlist.title', { defaultValue: 'Theo dõi' })}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#3b82f6', fontWeight: 500 }}>
              {t('earlyPivot.watchlist.subtitle', { defaultValue: 'Setup hình thành — chờ xác nhận trigger' })}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`${watching.length} ${t('earlyPivot.watchlist.stocks', { defaultValue: 'cổ phiếu' })}`}
          size="small"
          sx={{
            bgcolor: '#3b82f6',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 26,
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 850 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyLeft(0),
                  ...stickyLayer,
                  zIndex: STICKY_HEADER_Z,
                  width: 36, minWidth: 36, maxWidth: 36,
                  ...thSx,
                  borderRight: '1px solid #bfdbfe',
                }}
              >
                #
              </TableCell>
              <TableCell
                sx={{
                  ...stickyLeft(36),
                  ...stickyLayer,
                  zIndex: STICKY_HEADER_Z,
                  width: 72, minWidth: 72,
                  ...thSx,
                  borderRight: '1px solid #bfdbfe',
                }}
              >
                TICKER
              </TableCell>
              <TableCell align="center" sx={{ ...thSx, width: 90 }}>
                % SETUP
              </TableCell>
              <TableCell align="center" sx={{ ...thSx, minWidth: 96 }}>
                {t('earlyPivot.tableMain.colAi', { defaultValue: 'AI' })}
              </TableCell>
              <TableCell sx={{ ...thSx, minWidth: 140 }}>
                {t('earlyPivot.tableMain.colCompany', { defaultValue: 'CÔNG TY' })}
              </TableCell>
              <TableCell align="right" sx={{ ...thSx, width: 72 }}>
                CLOSE
              </TableCell>
              <TableCell align="right" sx={{ ...thSx, width: 68 }}>
                VOLUME
              </TableCell>
              <TableCell sx={{ ...thSx, minWidth: 160 }}>
                {t('earlyPivot.tableMain.colWaiting', { defaultValue: 'CHỜ XÁC NHẬN' })}
              </TableCell>
              <TableCell sx={{ ...thSx, width: 120 }}>
                {t('earlyPivot.tableMain.colItems', { defaultValue: 'ITEMS' })}
              </TableCell>
              <TableCell align="right" sx={{ ...thSx, width: 64 }}>
                RISK
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {watching.map((row, i) => {
              const waitingKeys = getWaitingForKeys(row) || [];
              return (
                <TableRow
                  key={row.ticker}
                  className="early-pivot-row"
                  sx={{
                    bgcolor: '#fafbfc',
                    borderBottom: '1px solid #e2e8f0',
                    transition: 'all 0.12s',
                    '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.04)' },
                    '&:hover .ep-sticky': { backgroundColor: STICKY_BG_HOVER },
                  }}
                >
                  <TableCell
                    className="ep-sticky"
                    sx={{
                      ...cellDense,
                      ...stickyLeft(0),
                      ...stickyLayer,
                      zIndex: STICKY_BODY_Z,
                      backgroundColor: stickyBodyBg(false),
                      color: '#64748b',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      width: 36, minWidth: 36,
                      borderRight: '1px solid #e2e8f0',
                    }}
                  >
                    {i + 1}
                  </TableCell>
                  <TableCell
                    className="ep-sticky"
                    sx={{
                      ...cellDense,
                      ...stickyLeft(36),
                      ...stickyLayer,
                      zIndex: STICKY_BODY_Z,
                      backgroundColor: stickyBodyBg(false),
                      width: 72, minWidth: 72,
                      borderRight: '1px solid #e2e8f0',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: '#334155', fontSize: '0.85rem' }}>
                      {row.ticker}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={cellDense}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 32, height: 5, bgcolor: '#e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ width: `${row.setup_score}%`, height: '100%', bgcolor: row.setup_score >= 80 ? '#3b82f6' : '#94a3b8' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>
                        {row.setup_score}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={cellDense}>
                    <AIConfidenceBadge
                      confidence={row.ai_confidence}
                      language={i18n.language}
                      tooltipTitle={t(
                        'earlyPivot.tableMain.aiConfTooltip',
                        'AI confidence \u2014 pattern reliability win probability',
                      )}
                      compact
                    />
                  </TableCell>
                  <TableCell sx={cellDense}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.3 }}>{row.companyName || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellDense, fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>
                    {row.close != null ? Number(row.close).toLocaleString('vi-VN') : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellDense, fontVariantNumeric: 'tabular-nums', fontSize: '0.72rem', color: '#94a3b8' }}>
                    {formatVolume(row.volume)}
                  </TableCell>
                  <TableCell sx={cellDense}>
                    {waitingKeys.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {waitingKeys.map((k) => (
                          <Chip
                            key={k}
                            size="small"
                            icon={<WatchLater sx={{ fontSize: 10 }} />}
                            label={t(`earlyPivot.waiting.${k}`, { defaultValue: k })}
                            sx={{
                              height: 20,
                              bgcolor: '#fef3c7',
                              color: '#92400e',
                              fontWeight: 600,
                              fontSize: '0.6rem',
                              '& .MuiChip-icon': { color: '#d97706', fontSize: 10 },
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={cellDense}>
                    {row.trigger_type ? (
                      <Chip
                        size="small"
                        label={row.trigger_type.toUpperCase()}
                        sx={{
                          height: 20,
                          bgcolor: '#f1f5f9',
                          color: '#475569',
                          fontWeight: 600,
                          fontSize: '0.62rem',
                          fontFamily: 'monospace',
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={cellDense}>
                    {(() => {
                      const v = row.risk_pct != null ? Number(row.risk_pct) * 100 : null;
                      const color = v != null ? (v < 5 ? '#059669' : v < 10 ? '#ca8a04' : '#dc2626') : '#94a3b8';
                      return (
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
                          {formatRiskPct(row.risk_pct)}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default WatchlistTable;
